import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import { ProductVariantNameCell, useSnackbar } from "@/features/ui";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormHelperText, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect } from "react";
import { formatNumber } from "@/features/shared";

export const ProductVariantsSection = () => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { showSnackbar } = useSnackbar();

  const { update, remove } = useFieldArray({
    control,
    name: "purchaseOrderProductVariants",
  });

  const productVariants = useWatch({
    control,
    name: "purchaseOrderProductVariants",
  });

  useEffect(() => {
    const subtotal = productVariants.reduce((sum, variant) => {
      return sum + (variant.quantity * variant.costPerUnit - variant.discount);
    }, 0);

    setValue("subtotal", subtotal);
  }, [productVariants, setValue]);

  const handleDeleteClick = (id) => () => {
    // 🔍 Find index of the row with the matching id
    const index = productVariants.findIndex((variant) => variant.id === id);

    if (index !== -1) {
      remove(index);
      showSnackbar("Produk berhasil dihapus", "success");
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Produk Varian",
      flex: 7,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} />
      ),
    },
    {
      field: "quantity",
      headerName: "Qty",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
      editable: true,
      flex: 1,
    },
    { field: "unitOfMeasurement", headerName: "Unit", flex: 1 },
    {
      field: "costPerUnit",
      headerName: "@Harga",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
      editable: true,
      flex: 2,
    },
    {
      field: "discount",
      headerName: "Diskon",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
      editable: true,
      flex: 2,
      valueSetter: (value, row) => {
        const newValue = value == null ? 0 : value; // Convert null or undefined to 0
        return { ...row, discount: newValue };
      },
    },
    {
      field: "totalPrice",
      headerName: "Total Harga",
      type: "number",
      flex: 2,
      valueGetter: (value, row) => {
        const { quantity, costPerUnit, discount } = row;

        return quantity * costPerUnit - discount || 0;
      },
    },
    {
      field: "actions",
      type: "actions",
      cellClassName: "actions",
      flex: 1,
      getActions: ({
        id,
        row: { receivedQuantity, salesRequestProductVariant },
      }) => {
        // Delete rules:
        // 1. receivedQuantity must be 0
        // 2. salesRequestProductVariant.orderedQuantity must be 0
        const orderedQty = salesRequestProductVariant?.orderedQuantity || 0;
        const canDelete = receivedQuantity === 0 && orderedQty === 0;

        let disabledReason = "";
        if (receivedQuantity > 0 && orderedQty > 0) {
          disabledReason =
            "Produk tidak dapat dihapus karena sudah diterima dan sudah dipesan";
        } else if (receivedQuantity > 0) {
          disabledReason = "Produk tidak dapat dihapus karena sudah diterima";
        } else if (orderedQty > 0) {
          disabledReason = "Produk tidak dapat dihapus karena sudah dipesan";
        }

        return [
          <Tooltip key={id} title={disabledReason}>
            <span>
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Hapus"
                className="textPrimary"
                onClick={handleDeleteClick(id)}
                color="error"
                disabled={!canDelete}
              />
            </span>
          </Tooltip>,
        ];
      },
    },
  ];

  const processRowUpdate = async (newRow) => {
    return new Promise((resolve, reject) => {
      // Clearing a numeric cell commits null (MUI's numeric valueParser), which
      // slips past the guards below and reaches the payload. Coerce to 0 first.
      const row = {
        ...newRow,
        quantity: Number(newRow.quantity) || 0,
        costPerUnit: Number(newRow.costPerUnit) || 0,
        discount: Number(newRow.discount) || 0,
      };
      const {
        quantity,
        costPerUnit,
        discount,
        receivedQuantity,
        salesRequestProductVariant,
      } = row;
      const totalCost = quantity * costPerUnit;
      const orderedQty = salesRequestProductVariant?.orderedQuantity || 0;

      // Edit rules:
      // 1. quantity must be >= receivedQuantity
      // 2. quantity must be >= salesRequestProductVariant.orderedQuantity
      const minQuantity = Math.max(1, receivedQuantity, orderedQty);
      if (quantity < minQuantity) {
        if (receivedQuantity > 0 && orderedQty > 0) {
          reject(
            new Error(
              `Kuantitas tidak boleh kurang dari ${minQuantity} (sudah diterima: ${receivedQuantity}, sudah dipesan: ${orderedQty})`
            )
          );
        } else if (receivedQuantity > 0) {
          reject(
            new Error(
              `Kuantitas tidak boleh kurang dari ${minQuantity} (sudah diterima)`
            )
          );
        } else if (orderedQty > 0) {
          reject(
            new Error(
              `Kuantitas tidak boleh kurang dari ${minQuantity} (sudah dipesan)`
            )
          );
        } else {
          reject(new Error("Kuantitas tidak boleh kurang dari 1"));
        }
        return;
      }

      if (costPerUnit < 0) {
        reject(new Error("Harga tidak boleh kurang dari 0"));
        return;
      }

      if (discount < 0) {
        reject(new Error("Diskon tidak boleh kurang dari 0"));
        return;
      }

      if (discount > totalCost) {
        reject(new Error("Diskon tidak boleh lebih besar dari Total Harga"));
        return;
      }

      // Find the index and update the form state
      const index = productVariants.findIndex(
        (variant) => variant.id === row.id
      );

      if (index !== -1) {
        update(index, row);
        resolve(row);
      } else {
        reject(new Error("Produk tidak ditemukan. Muat ulang halaman lalu coba lagi."));
      }
    });
  };

  const handleProcessRowUpdateError = (error) => {
    showSnackbar(error.message, "error");
  };

  const hasAnyConstraints = productVariants.some(
    (variant) =>
      (variant.receivedQuantity || 0) > 0 ||
      (variant.salesRequestProductVariant?.orderedQuantity || 0) > 0
  );

  return (
    <Stack spacing={1}>
      <DataGrid
        getRowHeight={() => "auto"}
        editMode="row"
        columns={columns}
        rows={productVariants}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        isCellEditable={() => true}
        initialState={{
          sorting: {
            sortModel: [{ field: "name", sort: "asc" }],
          },
        }}
      />
      {hasAnyConstraints && (
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Produk yang sudah diterima atau dipesan memiliki batasan untuk dihapus
          atau diubah kuantitasnya.
        </Typography>
      )}
      {errors.purchaseOrderProductVariants && (
        <FormHelperText error>
          {errors.purchaseOrderProductVariants.message}
        </FormHelperText>
      )}
    </Stack>
  );
};
